/**
 * Expiration d'un sondage de dates : jamais avant la dernière date proposée.
 *
 * Constat (enquête A7, 2026-10-02) : un créateur invité a fixé deux fois une
 * expiration ANTÉRIEURE aux dates de son sondage (07/09 puis 04/09 pour des
 * dates des 09 et 10/09), puis l'a recréé pour corriger. Règle :
 *   - expiration ≥ lendemain de la dernière date proposée, minuit local ;
 *   - par défaut, exactement ce lendemain (auparavant : création + 30 jours,
 *     sans lien avec les dates).
 * Module pur : partagé par l'éditeur (buildPollSettings), le chemin IA
 * (toPollDataJson) et les champs de saisie (message d'erreur).
 */

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function parseLocalDate(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function toIsoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

/** Dernière date proposée (YYYY-MM-DD), ou null. */
export function lastProposedDate(
  dates: readonly string[] | null | undefined,
): string | null {
  const valid = (dates ?? []).filter((d) => ISO_DATE_RE.test(d)).sort();
  return valid.length ? valid[valid.length - 1] : null;
}

/** Premier jour où le sondage peut expirer : lendemain de la dernière date. */
export function minExpiresAtDate(
  dates: readonly string[] | null | undefined,
): string | null {
  const last = lastProposedDate(dates);
  if (!last) return null;
  const d = parseLocalDate(last);
  d.setDate(d.getDate() + 1);
  return toIsoDate(d);
}

/** Expiration par défaut (ISO) : lendemain de la dernière date, minuit local. */
export function defaultExpiresAt(
  dates: readonly string[] | null | undefined,
): string | null {
  const min = minExpiresAtDate(dates);
  return min ? parseLocalDate(min).toISOString() : null;
}

/** Vrai si l'expiration tombe avant le lendemain de la dernière date. */
export function isExpiresAtTooEarly(
  dates: readonly string[] | null | undefined,
  expiresAt: string | null | undefined,
): boolean {
  if (!expiresAt) return false;
  const min = defaultExpiresAt(dates);
  if (!min) return false;
  const t = new Date(expiresAt).getTime();
  return Number.isFinite(t) && t < new Date(min).getTime();
}

/**
 * Expiration à enregistrer : le choix de l'utilisateur s'il est valide, sinon
 * le défaut. Filet de sécurité — l'interface refuse déjà la saisie trop tôt
 * avec un message ; ceci garantit la règle sur tous les chemins.
 */
export function resolveExpiresAt(
  dates: readonly string[] | null | undefined,
  requested: string | null | undefined,
): string | undefined {
  if (requested && !isExpiresAtTooEarly(dates, requested)) return requested;
  return defaultExpiresAt(dates) ?? requested ?? undefined;
}
